import { useRef, useEffect, useMemo } from "react";
import { debounce } from "lodash";

/**
 * Type for a generic function with no arguments
 */
type CallbackFunction = () => void;

/**
 * Custom hook that adds debounce to callback functions to prevent rapid consecutive executions
 * 
 * @param callback - The function to debounce
 * @returns A debounced version of the provided callback function
 */
const useDebounce = (callback: CallbackFunction): CallbackFunction => {
  // Create a ref to store the latest callback
  const ref = useRef<CallbackFunction | undefined>(callback);
  
  // Update the ref when the callback changes
  useEffect(() => {
    ref.current = callback;
  }, [callback]);
  
  // Create the debounced function (only once)
  const debouncedCallback = useMemo(() => {
    const func = () => {
      // Call the current callback from the ref (if it exists)
      ref.current?.();
    };
    
    // Return the debounced version of the function (500ms delay)
    return debounce(func, 500);
  }, []);
  
  return debouncedCallback;
};

export default useDebounce;
